import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import StatusBadge from './StatusBadge';
import SocialProof from './SocialProof';
import { resolveImageUrl } from './ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * HorizontalRow
 * -------------
 * Netflix-style horizontally-scrolling row of story cards. Used on the
 * homepage for Trending Now, New This Week, Most Saved, etc.
 *
 * Pulls its own data from /api/stories with the passed `params` object
 * (e.g. {sort: 'trending', channel: 'dreams_league', limit: 12}). Renders
 * nothing if the API returns 0 items so empty rows don't clutter the page.
 *
 * Scroll-snap on the inner container makes touch flicks feel native; arrow
 * buttons on the wrapper appear on hover at ≥md.
 */
const HorizontalRow = ({
  kicker,
  title,
  params = {},
  storiesPath = '/stories',
  testid,
  badgeKind = null,           // override badge derivation (e.g. always show TRENDING)
  emptyHidden = true,
  theme = 'light',            // 'light' (homepage) | 'dark' (Stories tab)
  limit,                      // hard cap on rendered cards regardless of API result
  onCardClick,                // when set, cards render as buttons that fire this instead of navigating
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const qs = new URLSearchParams(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
        ).toString();
        const r = await axios.get(`${API}/stories?${qs}`);
        setItems(r.data || []);
      } catch {
        setItems([]);
      } finally { setLoading(false); }
    };
    fetchData();
    // params is intentionally serialized via deps below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);

  if (loading) return null;
  if (emptyHidden && items.length === 0) return null;

  // Apply optional hard cap AFTER the empty-hidden check so limit doesn't
  // accidentally hide a working row.
  const cards = typeof limit === 'number' ? items.slice(0, limit) : items;

  const isDark = theme === 'dark';
  const cls = {
    kicker:  isDark ? 'text-blue-300' : 'text-gray-500',
    title:   isDark ? 'text-white' : 'text-black',
    arrowBtn: isDark
      ? 'border-white/15 text-white/70 hover:bg-white hover:text-black'
      : 'border-black/15 hover:bg-black hover:text-white',
    cardBg:  isDark ? 'bg-white/[0.03] ring-1 ring-white/10' : 'bg-gray-100',
    category: isDark ? 'text-white/50' : 'text-gray-500',
    heading: isDark ? 'text-white group-hover:text-blue-200' : 'text-black group-hover:text-gray-700',
    subtext: isDark ? 'text-white/50' : 'text-gray-500',
  };

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.85;
    el.scrollBy({ left: dir === 'next' ? step : -step, behavior: 'smooth' });
  };

  return (
    <section className="py-10" data-testid={testid}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            {kicker && (
              <p className={`font-italic text-[11px] uppercase tracking-[0.22em] mb-1 ${cls.kicker}`}>{kicker}</p>
            )}
            <h2 className={`font-display text-2xl sm:text-3xl uppercase leading-none ${cls.title}`}>{title}</h2>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scroll('prev')}
              className={`w-9 h-9 rounded-full border transition flex items-center justify-center ${cls.arrowBtn}`}
              aria-label="Scroll left"
              data-testid={`${testid}-prev`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => scroll('next')}
              className={`w-9 h-9 rounded-full border transition flex items-center justify-center ${cls.arrowBtn}`}
              aria-label="Scroll right"
              data-testid={`${testid}-next`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3"
          style={{ scrollbarWidth: 'none' }}
        >
          {cards.map((story) => {
            const cardBody = (
              <>
                <div className={`relative aspect-[4/5] rounded-2xl overflow-hidden ${cls.cardBg}`}>
                  <img
                    src={resolveImageUrl(story.thumbnail || story.cover_image, 400)}
                    alt={story.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3">
                    <StatusBadge story={story} kind={badgeKind} />
                  </div>
                </div>
                <div className="mt-3 text-left">
                  <p className={`text-[10px] font-bold uppercase tracking-widest ${cls.category}`}>{story.category}</p>
                  <h3 className={`font-display text-lg uppercase leading-tight mt-1 transition line-clamp-2 ${cls.heading}`}>{story.name}</h3>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className={`text-xs ${cls.subtext}`}>{story.author} · {formatDate(story.publish_date)}</p>
                    <SocialProof story={story} />
                  </div>
                </div>
              </>
            );
            // iter484 · Light haptic when tapping a written-story
            // card so opening a story feels tactile on native iOS.
            const onStoryTap = () => {
              try { import('../lib/nativeBridge').then((m) => m.haptic('light')).catch(() => {}); } catch { /* noop */ }
            };
            const commonProps = {
              key: story.id,
              className: 'group snap-start flex-shrink-0 w-[72vw] sm:w-[42vw] md:w-[300px] lg:w-[320px]',
              'data-testid': `${testid}-card-${story.slug}`,
            };
            return onCardClick
              ? <button type="button" onClick={() => { onStoryTap(); onCardClick(story); }} {...commonProps}>{cardBody}</button>
              : <Link to={`${storiesPath}/${story.slug}`} onClick={onStoryTap} {...commonProps}>{cardBody}</Link>;
          })}
        </div>
      </div>
    </section>
  );
};

export default HorizontalRow;
